// Takes in a buy order with id, status, and other details

import { useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AppealKind, AppealKindOptions } from '@/constants/common'
import { BuyOrder } from '@/types/common'
import { cn } from '@/utils/cn'
import { ArrowBack, Close } from '@/components/icon'
import { Stack } from '@/components/layout'
import { AmountMismatchForm } from '@/components/orders/appeal/AmountMismatchForm'
import { AppealDetailsForm } from '@/components/orders/appeal/DetailsForm'
import { AppealKindSelector } from '@/components/orders/appeal/KindSelector'
import ScreenHeader from '@/components/ScreenHeader'

type step = 'selectKind' | 'details' | 'amountMismatch'

export function AppealRequest({
  buyOrder,
  isBuyer,
  cancelAppeal,
  completeAppeal
}: {
  buyOrder: BuyOrder
  isBuyer: boolean
  cancelAppeal: () => void
  completeAppeal: () => void
}) {
  const [appealKind, setAppealKind] = useState<AppealKind | null>(null)
  const [currentStep, setCurrentStep] = useState<step>('selectKind')
  const { t } = useTranslation()
  const { top } = useSafeAreaInsets()

  const selectAppealKind = (kind: AppealKindOptions) => {
    if (kind === 'AmountMismatch') {
      setCurrentStep('amountMismatch')
    } else {
      setAppealKind(kind)
      setCurrentStep('details')
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => (
            <View className={'bg-background-primary'} style={{ paddingTop: top }}>
              <ScreenHeader
                hasBottomBorder={currentStep !== 'amountMismatch'}
                leftElement={
                  currentStep !== 'selectKind' && (
                    <View>
                      <Pressable className={'flex-row p-2'} onPress={() => setCurrentStep('selectKind')}>
                        {({ pressed }) => (
                          <>
                            <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />
                            <Text className={cn('text-base font-medium text-text-tertiary', pressed && 'opacity-50')}>
                              {t('orderAppeal:back')}
                            </Text>
                          </>
                        )}
                      </Pressable>
                    </View>
                  )
                }
                centerElement={
                  <Text className={cn('text-base font-medium text-text-primary')}>
                    {t(`orderAppeal:title.${currentStep}`)}
                  </Text>
                }
                rightElement={
                  <Pressable className={'p-2'} onPress={() => cancelAppeal()}>
                    {({ pressed }) => <Close className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
                  </Pressable>
                }
              />
            </View>
          )
        }}
      />
      {currentStep === 'selectKind' && (
        <AppealKindSelector buyOrder={buyOrder} isBuyer={isBuyer} selectAppealKind={selectAppealKind} />
      )}
      {currentStep === 'details' && (
        <AppealDetailsForm data={buyOrder} appealKind={appealKind as AppealKind} completeAppeal={completeAppeal} />
      )}
      {currentStep === 'amountMismatch' && <AmountMismatchForm data={buyOrder} completeAppeal={completeAppeal} />}
    </>
  )
}
